import { useEffect, useRef, useState } from "react";
import type { TamanhoPagina } from "@/hooks/usePaginacaoLocal";

interface Controlado {
  pagina: number;
  tamanhoPagina: number;
  /** Recebe a nova página/tamanho (ex.: `setFiltros` do `useFiltrosNaUrl`). */
  onChange: (p: { pagina: number; tamanhoPagina: number }) => void;
}

interface Opcoes {
  /** Estado externo (URL). Sem ele o hook guarda página/tamanho sozinho. */
  controlado?: Controlado;
  tamanhoInicial?: TamanhoPagina;
  /** Volta para a página 1 quando algum destes valores muda (filtros, busca, ordenação). */
  resetarQuando?: unknown[];
}

/** Metadados devolvidos pelo backend em `PaginadoResponse<T>`. */
interface RespostaPaginada {
  total: number;
  totalPaginas: number;
}

/**
 * Paginação feita no SERVIDOR (o par do `usePaginacaoLocal`). Uso:
 *
 *   const pag = usePaginacaoServidor({ resetarQuando: [busca, situacao] });
 *   const { data } = useQuery({ queryKey: [..., pag.pagina, pag.tamanhoPagina], ... });
 *   <PaginacaoRodape {...pag.rodape(data)} />
 */
export function usePaginacaoServidor({ controlado, tamanhoInicial = 20, resetarQuando = [] }: Opcoes = {}) {
  const [paginaInterna, setPaginaInterna] = useState(1);
  const [tamanhoInterno, setTamanhoInterno] = useState<number>(tamanhoInicial);

  const pagina        = controlado?.pagina ?? paginaInterna;
  const tamanhoPagina = controlado?.tamanhoPagina ?? tamanhoInterno;

  const mudar = (p: { pagina: number; tamanhoPagina: number }) => {
    if (controlado) controlado.onChange(p);
    else { setPaginaInterna(p.pagina); setTamanhoInterno(p.tamanhoPagina); }
  };

  // Filtro mudou → página 1 (não na montagem: a página pode ter vindo da URL).
  const chave = JSON.stringify(resetarQuando);
  const anterior = useRef(chave);
  useEffect(() => {
    if (anterior.current === chave) return;
    anterior.current = chave;
    if (pagina !== 1) mudar({ pagina: 1, tamanhoPagina });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  const setPagina        = (p: number) => mudar({ pagina: Math.max(1, p), tamanhoPagina });
  const setTamanhoPagina = (t: number) => mudar({ pagina: 1, tamanhoPagina: t });

  /** Props do `PaginacaoRodape` a partir da resposta do servidor (pode estar carregando). */
  const rodape = (resp?: RespostaPaginada | null) => {
    const totalItens   = resp?.total ?? 0;
    const totalPaginas = Math.max(1, resp?.totalPaginas ?? 1);
    const paginaVisivel = Math.min(pagina, totalPaginas);
    return {
      pagina: paginaVisivel,
      totalPaginas,
      totalItens,
      primeiroItem: totalItens === 0 ? 0 : (paginaVisivel - 1) * tamanhoPagina + 1,
      ultimoItem: Math.min(paginaVisivel * tamanhoPagina, totalItens),
      tamanhoPagina: tamanhoPagina as TamanhoPagina,
      onPaginaChange: (p: number) => setPagina(Math.min(p, totalPaginas)),
      onTamanhoPaginaChange: (t: TamanhoPagina) => setTamanhoPagina(t),
    };
  };

  return { pagina, tamanhoPagina, setPagina, setTamanhoPagina, rodape };
}
